;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();import get from "lodash/get"
import Image from "next/image"
import {
  LOCATION_ADDRESS_CITY,
  LOCATION_ADDRESS_STATE,
  LOCATION_ADDRESS_ZIP_CODE,
} from "../../../../lib/constants"
import guessAndCastDateTimeToLocal from "../../../../lib/formatter/guessAndCastDateTimeToLocal"
import { isVirtual } from "../../../../lib/cohort/inPersonOrRemote"
import {
  isPayPerSession,
  getRefundPolicyHours,
  getRefundPolicyPercent,
  getPrice,
  getPricePerSession,
} from "../../../../lib/pricing"
import PricePerSessionIcon from "../shared/icons/PricePerSessionIcon"
import PaymentPeriodIcon from "../shared/icons/PaymentPeriodIcon"
import NumberOfSessionsIcon from "../shared/icons/NumberOfSessionsIcon"
import DashedLineIcon from "../shared/icons/DashedLineIcon"
import MobileDashedLineIcon from "../shared/icons/MobileDashedLineIcon"
import { Cohort, Group, Pricing, SessionInfo } from "../types"

const LOCATION_STYLING = "text-[16px] tracking-[1.12px] leading-[23px]"
const BORDER_STYLING =
  "w-full border-b-[1px] border-t-0 border-l-0 border-r-0 border-[#BDBBBB] border-solid my-[7px]"
const MOBILE_BORDER_STYLING =
  "w-full border-t-[1px] border-b-0 border-l-0 border-r-0 border-[#F1F1F1] border-solid my-[8px] min-[800px]:hidden"
const ICON_CONTAINER = "flex min-[800px]:hidden"
const ICON_AND_TEXT_CONTAINER =
  "flex flex-row justify-between min-[800px]:justify-end items-center"

type Props = {
  group: Group
  cohort: Cohort
  latestPricing: Pricing
  sessionInfo: SessionInfo
}

export default function SeatTicketCard({
  group,
  cohort,
  latestPricing,
  sessionInfo,
}: Props) {
  const groupPhotoUrl = get(group, "photoUrl", "")
  const groupName = get(group, "name", "")
  const cohortLocation = get(cohort, "location", {})
  const cohortCity = get(cohortLocation, `[${LOCATION_ADDRESS_CITY}]`, "")
  const cohortState = get(cohortLocation, `[${LOCATION_ADDRESS_STATE}]`, "")
  const cohortZipCode =
    get(cohortLocation, `[${LOCATION_ADDRESS_ZIP_CODE}]`, "") || ""

  const isDefinite = get(sessionInfo, "isDefinite", false)
  const firstSessionDateCastToLocalTime = guessAndCastDateTimeToLocal(
    get(sessionInfo, "upcomingSessionStartsAt", ""),
  )
  const month = firstSessionDateCastToLocalTime.format("MMM")
  const day = firstSessionDateCastToLocalTime.format("D")
  const weekDay = firstSessionDateCastToLocalTime.format("ddd")
  const time = firstSessionDateCastToLocalTime.format("h:mmA")
  const isCohortVirtual = isVirtual({ cohort })

  const isPayPerSessionValue = isPayPerSession({ paymentObj: latestPricing })
  const remainingSessionCount = get(
    sessionInfo,
    "remainingSessionCount",
    0,
  ) as number
  const _refundPolicyHours = getRefundPolicyHours({ paymentObj: latestPricing })
  const _refundPolicyPercent = getRefundPolicyPercent({ paymentObj: latestPricing })
  const totalPrice = getPrice({ paymentObj: latestPricing })
  const price = getPricePerSession({
    paymentObj: latestPricing,
    remainingSessions: remainingSessionCount,
  })

  const numberToWords = (num: number) => {
    const words = [
      "zero",
      "one",
      "two",
      "three",
      "four",
      "five",
      "six",
      "seven",
      "eight",
      "nine",
      "ten",
    ]
    return words[num] || String(num)
  }

  const renderSessionsRemainingOrFrequency = () => {
    const weeklyFrequency = get(
      cohort,
      "schedule.recurring.windows_per_recurrence",
      0,
    ) as number
    const meetingsAWeek = get(cohort, "schedule.recurring.items.length", 0) as number
    let indefiniteFrequencyStr = ""
    if (!isDefinite) {
      switch (meetingsAWeek) {
        case 1:
          indefiniteFrequencyStr = "Meets once weekly"
          break
        case 2:
          indefiniteFrequencyStr = "Meets twice weekly"
          break
        default:
          if (meetingsAWeek >= 3)
            indefiniteFrequencyStr = `Meets ${numberToWords(meetingsAWeek)} times weekly`
          break
      }
    } else if (weeklyFrequency > 1) {
      const times = meetingsAWeek === 1 ? "time" : "times"
      indefiniteFrequencyStr = `Meets ${numberToWords(meetingsAWeek)} ${times} every ${numberToWords(weeklyFrequency)} weeks`
    }

    return isDefinite ? (
      <div
        className={"text-end mb-[10px]"}
      >{`${remainingSessionCount} ${remainingSessionCount > 1 ? "sessions" : "session"}`}</div>
    ) : (
      <div className={"text-end mb-[10px]"}>{indefiniteFrequencyStr}</div>
    )
  }

  const paymentText = isPayPerSessionValue
    ? "PAY AS YOU GO, SESSION BY SESSION"
    : "PAY IN FULL UPFRONT"

  return (
    <div className="bg-white rounded-[20px]  flex  flex flex-col grow p-[13px_10px] min-[600px]:p-[20px_20px]">
      <div className="font-bold text-[20px] tracking-[1px] mb-[20px] text-center min-[800px]:text-left">
        {groupName}
      </div>
      <div className="flex flex-col min-[800px]:flex-row">
        <div className={"flex flex-col items-center min-[800px]:items-start"}>
          <div className="mb-[29px]">
            <Image
              src={groupPhotoUrl}
              alt={"group image for checkout"}
              width={292}
              height={184}
              className={"rounded-[5px] min-[800px]:rounded-0"}
              priority={true}
            />
          </div>
          <div className={"flex flex-row w-[292px] min-[800px]:w-none"}>
            <div className="bg-[#00B2FF]/50 rounded-[11px] flex flex-col justify-between items-center w-[68px] h-[68px] mr-[15px]">
              <div className="font-medium text-[18px] tracking-[.36px] mt-[12px]">
                {month}
              </div>
              <div className={"text-[#FC86C3] mb-[12px]"}>{day}</div>
            </div>
            <div className="flex flex-col">
              <div className="text-[18px] tracking-[.54px]">
                {`${weekDay} @ `}
                <span className="font-bold">{time}</span>
              </div>
              {isCohortVirtual ? (
                <div className={LOCATION_STYLING}>Meets virtually</div>
              ) : (
                <div
                  className={LOCATION_STYLING}
                >{`${cohortCity}, ${cohortState} ${cohortZipCode}`}</div>
              )}
            </div>
          </div>
        </div>

        <div className="mx-[20px] overflow-y-hidden hidden min-[800px]:block">
          <DashedLineIcon />
        </div>
        <div key="session-dets" className="flex flex-col justify-between grow">
          <div className="flex flex-col">
            <div className="flex flex-col min-[800px]:flex-row justify-between min-[800px]:items-center grow-1 min-[800px]:grow-0">
              <div className="my-[5px] min-[800px]:hidden overflow-x-hidden w-full">
                <MobileDashedLineIcon />
              </div>
              <div className={"flex flex-row items-center justify-between"}>
                <div className="text-[#BDBBBB] border-[1px] border-[#BDBBBB] border-solid rounded-[10px] min-[800px]:w-[42px] min-[800px]:h-[42px] w-[38px] h-[38px] flex justify-center items-center mr-[8px]">
                  1
                </div>
                <div className="text-[#BDBBBB]">Seat reservation</div>
              </div>
              <div className={MOBILE_BORDER_STYLING} />
              <div className={ICON_AND_TEXT_CONTAINER}>
                <div className={ICON_CONTAINER}>
                  <PricePerSessionIcon />
                </div>
                <div>{`$${price} / session`}</div>
              </div>
            </div>
            <div className={MOBILE_BORDER_STYLING} />
            <div className={ICON_AND_TEXT_CONTAINER}>
              <div className={ICON_CONTAINER}>
                <NumberOfSessionsIcon />
              </div>
              {renderSessionsRemainingOrFrequency()}
            </div>
            <div className={MOBILE_BORDER_STYLING} />
            <div className={ICON_AND_TEXT_CONTAINER}>
              <div className={ICON_CONTAINER}>
                <PaymentPeriodIcon />
              </div>
              <div className={"text-end"}>{paymentText}</div>
            </div>
            <div className={MOBILE_BORDER_STYLING} />
          </div>
          <div className={"mt-[29px] min-[800px]:mt-0"}>
            <div className={BORDER_STYLING} />
            {isPayPerSessionValue ? (
              <>
                <div className="text-[#00B2FF] flex flex-row justify-between ">
                  <div>Payment due each session:</div>
                  <div>${price}</div>
                </div>
                <div className={BORDER_STYLING} />
              </>
            ) : null}
            <div className="flex flex-row justify-between font-bold text-[20px] mt-[10px]">
              <div>Due to your provider:</div>
              <div>${`${totalPrice}`}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
